import { memo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PauseCircleIcon from '@mui/icons-material/PauseCircleOutline';
import { PointsApplicationState } from 'root/src/server/modules/points/types/types';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { addHttpsProtocol } from 'root/src/shared/util/protocolUtil';
import { RedesignSponsorshipTerms } from 'root/src/client/v2/_pages/drop/redesign/RedesignSponsorshipTerms';
import { redesignSponsorshipTerms } from 'root/src/client/v2/_pages/drop/redesign/sponsorshipTerms';
import { RedesignChatAutoShareSection } from 'root/src/client/v2/_pages/drop/redesign/RedesignChatAutoShareSection';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useDropShortUrl } from 'root/src/client/v2/drop/hooks/useDropShortUrl';
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { CopyField } from 'root/src/client/v2/common/components/ui/copy-field';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const STATUS_META = {
    [PointsApplicationState.APPROVED]: { label: 'Approved', Icon: CheckCircleIcon, className: 'text-neon-green' },
    [PointsApplicationState.REJECTED]: { label: 'Rejected', Icon: CancelIcon, className: 'text-destructive' },
    [PointsApplicationState.PENDING]: { label: 'In review', Icon: ScheduleIcon, className: 'text-neon-amber' },
    [PointsApplicationState.ON_HOLD]: { label: 'On hold', Icon: PauseCircleIcon, className: 'text-muted-foreground' },
};
const StatTile = ({ label, value, valueClassName }) => (<div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-solid border-white/[0.06] bg-white/[0.03] px-2 py-3 text-center">
		<span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
		<span className={cn('mt-1 text-sm font-bold text-foreground', valueClassName)}>{value}</span>
	</div>);
export const RedesignSponsorshipDetailsModal = memo(({ drop }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const modal = useRedesignModal();
    const acceptedDares = (_a = drop.creatorAcceptedDares) !== null && _a !== void 0 ? _a : 0;
    // Only fetch/reveal the tracking share link AFTER the creator has accepted a dare - fetching it
    // generates/persists the link server-side, so gating the query prevents handing one out upfront.
    const shortUrlQuery = useDropShortUrl({ dropId: drop.id, enabled: acceptedDares > 0 });
    const maxDares = useMaxDaresByPlatforms({ fallbackDrop: drop });
    const totalForYou = Number.isFinite(maxDares) ? maxDares : ((_c = (_b = drop.dares) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0);
    // The scheduled stream date lives on the points application, not the drop (same source the legacy
    // modal reads). Only the approved-application bullet uses it, but the query drives that condition too.
    const pointsApplicationQuery = trpc.pointsApplication.getByDropId.useQuery({ dropId: drop.id });
    // Matches legacy `SponsorshipDetailsModal`: the terms are accepted exactly when a reservation row
    // exists for this creator. Without it the "Your points are reserved from X to Y" bullet never renders.
    const isTermsAccepted = Boolean(drop.termsAcceptedDate);
    const statusMeta = drop.pointsApplicationState ? STATUS_META[drop.pointsApplicationState] : undefined;
    const StatusIcon = statusMeta === null || statusMeta === void 0 ? void 0 : statusMeta.Icon;
    const statusValue = statusMeta && StatusIcon
        ? (<span className={cn('flex items-center justify-center gap-1', statusMeta.className)}>
				<StatusIcon sx={{ fontSize: 14 }}/>
				{statusMeta.label}
			</span>)
        : drop.pointsApplicationState;
    const stats = [
        { label: 'Dares', value: `${acceptedDares} / ${totalForYou}` },
        ...(drop.awardPoints && drop.estimatedPointsToEarn ? [{ label: 'Max points', value: formatNumberWithCommas(drop.estimatedPointsToEarn), valueClassName: 'text-neon-cyan' }] : []),
        ...(drop.pointsApplicationState ? [{ label: 'Status', value: statusValue }] : []),
    ];
    return (<RedesignModalShell className="sm:max-w-lg">
			<div className="flex w-full flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Sponsorship details</h2>
				<p className="text-sm text-muted-foreground">Your participation in <span className="font-semibold text-foreground">{(_d = drop.brand) === null || _d === void 0 ? void 0 : _d.brandName}</span>&apos;s drop.</p>
			</div>

			<div className="flex w-full gap-2">
				{stats.map(stat => <StatTile key={stat.label} label={stat.label} value={stat.value} valueClassName={stat.valueClassName}/>)}
			</div>

			<div className="w-full">
				<p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">How to earn points</p>
				<div className="rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4">
					<RedesignSponsorshipTerms terms={redesignSponsorshipTerms} dropEvent={drop} shortUrl={(_e = shortUrlQuery.data) !== null && _e !== void 0 ? _e : ''} canEarnPoints={Boolean(drop.awardPoints)} isTermsAccepted={isTermsAccepted} canUserReclaimReservation={Boolean(drop.canUserReclaimReservation)} streamDate={(_f = pointsApplicationQuery.data) === null || _f === void 0 ? void 0 : _f.streamDate} pointsReservationDueDate={drop.pointsReservationDueDate}/>
				</div>
			</div>

			{acceptedDares > 0 && shortUrlQuery.data && (<>
					<div className="w-full">
						<p className="mb-1 text-[11px] font-medium text-muted-foreground">Your share link</p>
						<CopyField value={shortUrlQuery.data} className="w-full"/>
					</div>
					{/* Same gating as legacy: the auto-share controls exist only once the share link does. */}
					<RedesignChatAutoShareSection dropId={drop.id} shareUrl={(_g = addHttpsProtocol(shortUrlQuery.data)) !== null && _g !== void 0 ? _g : shortUrlQuery.data} linkMarketingCopy={drop.linkMarketingCopy}/>
				</>)}

			<Button variant="brand" onClick={modal.hide} className="w-full max-w-xs rounded-lg text-sm font-semibold">Close</Button>
		</RedesignModalShell>);
});
